import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import DigestRecipients from './DigestRecipients';
import { Loader2, Send, CalendarClock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WeeklyDigestDialog({ open, onOpenChange }) {
  const [html, setHtml] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/kpi/digest/preview`, { responseType: 'text' }),
      axios.get(`${API_URL}/kpi/digest/history`),
    ])
      .then(([p, h]) => { setHtml(p.data); setHistory(h.data || []); })
      .catch(() => toast.error('Could not load digest preview'))
      .finally(() => setLoading(false));
  }, [open]);

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/kpi/digest/send`);
      if (res.data.sent > 0) toast.success(`Digest sent to ${res.data.sent} recipient${res.data.sent === 1 ? '' : 's'}`);
      else toast.warning(res.data.reason === 'no_data' ? 'No KPI data to summarize yet' : `Digest not sent: ${res.data.status || res.data.reason}`);
      const h = await axios.get(`${API_URL}/kpi/digest/history`);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest');
    } finally { setSending(false); }
  };

  const last = history[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="weekly-digest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-orange-500" />Weekly KPI Digest
          </DialogTitle>
          <DialogDescription>
            Emailed automatically every <b>Monday at 8:00 AM Atlantic</b>, built from the latest uploaded week.
            {last && <span className="block mt-1 text-xs" data-testid="digest-last-sent">Last run: {new Date(last.sent_at).toLocaleString()} · {last.status}{last.week ? ` · week ${last.week}` : ''}{last.recipients ? ` · ${last.recipients.length} recipient${last.recipients.length === 1 ? '' : 's'}` : ''}</span>}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="preview" className="flex-1 min-h-0 flex flex-col">
          <TabsList className="self-start">
            <TabsTrigger value="preview" data-testid="digest-tab-preview">Preview</TabsTrigger>
            <TabsTrigger value="recipients" data-testid="digest-tab-recipients">Recipients</TabsTrigger>
          </TabsList>
          <TabsContent value="preview" className="flex-1 min-h-0 mt-3">
            <div className="h-full border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
              {loading ? (
                <div className="h-64 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Building preview…</div>
              ) : (
                <iframe title="Weekly KPI digest preview" srcDoc={html} className="w-full h-[55vh] bg-white" data-testid="digest-preview-frame" />
              )}
            </div>
          </TabsContent>
          <TabsContent value="recipients" className="flex-1 min-h-0 mt-3 overflow-y-auto max-h-[55vh] pr-1">
            <DigestRecipients />
          </TabsContent>
        </Tabs>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Close</Button>
          <Button onClick={sendNow} disabled={sending || loading} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="digest-send-now-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
